import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';

import { fetchRequestStart } from '../../store/request/request.actions';
import { RootState } from '../../store/root-reducer';
import { DealType, RouteParams } from '../../store/types/types';
import ProgressSpinner from '../progress-spinner/progress-spinner.component';
import RequestGrid from '../request-grid/request-grid.component';
import ErrorMessage from '../error/error.component';
import DeactivateConfirmModal from '../deactivate-modal/deactivate-modal.component';
import RejectConfirmModal from '../reject-modal/reject-modal.component';
import Notification from '../notification/notification.component';
import {
  StyledContainer,
  StyledBox,
  DeactivateButton,
  RejectButton
} from './deactivation-request.styles';


const DeactivationRequest = ({ programmatic }: DealType) => {
  const dispatch = useDispatch();
  const { reject, deactivate, request } = useSelector(
    (state: RootState) => state
  );

  const { message, isFetching, requestErrorMessage } = request;
  const { isDeactivated, deactivateErrorMessage } = deactivate;
  const { isRejected, rejectErrorMessage } = reject;

  const [isDeactivateOpen, setDeactivateOpen] = React.useState<boolean>(false);
  const [isRejectOpen, setRejectOpen] = React.useState<boolean>(false);
  const { messageId } = useParams<RouteParams>();

  const handleDeactivateOpen = () => setDeactivateOpen(true);
  const handleDeactivateClose = () => setDeactivateOpen(false);
  const handleRejectOpen = () => setRejectOpen(true);
  const handleRejectClose = () => setRejectOpen(false);

  useEffect(() => {
    dispatch(fetchRequestStart({ messageId, programmatic }))
  }, [messageId, dispatch, programmatic]);

  let deactivated = false;
  let cancelled = false;

  if (message) {
    deactivated = message.deactivated || message.actionTaken === "DEACTIVATED";
    cancelled = message.cancelled || message.actionTaken === "CANCELLED";
  }

  return (
    <StyledContainer maxWidth="md">
      <DeactivateConfirmModal
        isOpen={isDeactivateOpen}
        handleClose={handleDeactivateClose}
        messageId={messageId}
        programmatic={programmatic}
      />
      <RejectConfirmModal
        isOpen={isRejectOpen}
        handleClose={handleRejectClose}
        messageId={messageId}
      />
      {
        isDeactivated ? (
          <Notification alert="deactivate" status="success" />
        ) : deactivateErrorMessage ? (
          <Notification alert="deactivate" status="error" />
        ) : isRejected ? (
          <Notification alert="cancel" status="success" />
        ) : rejectErrorMessage ? (
          <Notification alert="cancel" status="error" />
        ) : deactivated ? (
          <Notification alert="deactivated" status="warning" />
        ) : cancelled ? (
          <Notification alert="cancelled" status="warning" />
        ) : null}
      {
        message ? (
          <Container
            component={Paper}
            sx={{ mt: 5, padding: 6 }}
          >
            <StyledBox>
              <DeactivateButton
                disabled={isDeactivated || isRejected || deactivated || cancelled}
                onClick={handleDeactivateOpen}
                variant="contained"
                size="medium"
                color="primary"
              >
                Deactivate
              </DeactivateButton>
              <RejectButton
                disabled={isDeactivated || isRejected || deactivated || cancelled}
                onClick={handleRejectOpen}
                variant="contained"
                size="medium"
                color="error"
              >
                Cancel
              </RejectButton>
            </StyledBox>
            <RequestGrid
              messageBody={message}
              programmatic={programmatic}
            />
          </Container>
        ) : isFetching ? (
          <ProgressSpinner />
        ) : requestErrorMessage ? (
          <ErrorMessage />
        ) : null}
    </StyledContainer>
  );
}

export default DeactivationRequest;
